// ForgetDialogee
// Forgot password via WhatsApp OTP: request code -> verify code -> set new password.


import * as React from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setForgetPasswordFalse } from '../../pages/redux/features/session/sessionslice';
import { logout } from '../../pages/redux/features/auth/authSlice';
import { mobileApiBaseURL } from '../../pages/redux/features/util/utilSlice';
import Input from '../Input/Input';
import { executeApi } from '../../utils/WithAuth';
import { variables } from '../../utils/config';
import PopupAlert from '../PopupAlert/PopupAlert';

const RESEND_SECONDS = 60;
const OTP_LIFETIME_SECONDS = 5 * 60;

const formatCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const GENERIC_ERROR = "Something went wrong. Please try again.";

// These endpoints answer 400/429/500 with a useful `message`, so call axios directly
// instead of executeApi (which replaces every failure with a generic error and treats 401 as session expiry).
const postForgotPassword = async (url, body) => {
  try {
    const res = await axios({ method: 'POST', url, data: body, validateStatus: () => true });
    const json = res.data || {};
    return {
      code: res.status,
      ok: res.status === 200 && json.status === true,
      // A ProblemDetails 400 has no `message` key.
      message: typeof json.message === 'string' && json.message ? json.message : GENERIC_ERROR,
      data: json.data || null,
    };
  } catch (error) {
    console.log(error.message);
    return { code: 0, ok: false, message: GENERIC_ERROR, data: null };
  }
};

const buttonStyle = { color: "#fff", backgroundColor: "#e46e39" };
// Inline colors override MUI's disabled look, so fade disabled buttons ourselves.
const primaryStyle = (disabled) => ({ ...buttonStyle, opacity: disabled ? 0.6 : 1 });
const spinner = <CircularProgress size={16} color="inherit" style={{ marginRight: "8px" }} />;
const outlinedStyle = { color: "#e46e39", border: "1px solid #e46e39" };

export default function ForgetDialogee() {
  const { forgetPassword } = useSelector(state => state.sessionExpired)
  const baseURL = useSelector((state) => state.baseURL);
  const [step, setStep] = React.useState("request"); // request | verify | reset
  const [loadingAction, setLoadingAction] = React.useState(""); // "" | request | verify | reset
  const loading = loadingAction !== "";
  const [mobileNo, setMobileNo] = React.useState("");
  const [companies, setCompanies] = React.useState([]);
  const [selectedCompany, setSelectedCompany] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [resetToken, setResetToken] = React.useState("");
  const [newPass, setNewPass] = React.useState("");
  const [confirmPass, setConfirmPass] = React.useState("");
  const [resendIn, setResendIn] = React.useState(0);
  const [otpExpiresIn, setOtpExpiresIn] = React.useState(0);
  const [message, setMessage] = React.useState("");
  const [severty, setSeverty] = React.useState("");
  const [showPopup, setShowPopup] = React.useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const showAlert = (sev, msg) => {
    setSeverty(sev);
    setMessage(msg);
    setShowPopup(true);
  };

  // Company list for the entered mobile number, same lookup as the sign-in page.
  React.useEffect(() => {
    if (!forgetPassword || step !== "request") return;
    const mobile = mobileNo.trim();
    if (mobile.length < 4) {
      setCompanies([]);
      setSelectedCompany("");
      return;
    }
    const timeout = setTimeout(() => {
      executeApi(`${baseURL}/v1/Auth/companiesByPhone?mobileNo=${mobile}`, {}, "GET", null, dispatch)
        .then((res) => {
          const list = Array.isArray(res) ? res : [];
          setCompanies(list);
          setSelectedCompany(list.length === 1 ? list[0] : "");
        })
        .catch((err) => {
          console.log(err.message);
          setCompanies([]);
          setSelectedCompany("");
        });
    }, 500);
    return () => clearTimeout(timeout);
  }, [mobileNo, forgetPassword, step, baseURL, dispatch]);

  // "Resend code" countdown.
  React.useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // OTP lifetime countdown (code expires 5 minutes after it is sent).
  React.useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const timer = setTimeout(() => setOtpExpiresIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpExpiresIn]);

  const resetState = () => {
    setStep("request");
    setLoadingAction("");
    setMobileNo("");
    setCompanies([]);
    setSelectedCompany("");
    setOtp("");
    setResetToken("");
    setNewPass("");
    setConfirmPass("");
    setResendIn(0);
    setOtpExpiresIn(0);
  };

  const handleCloseD = () => {
    resetState();
    dispatch(setForgetPasswordFalse())
  }

  const requestOtp = async () => {
    if (!mobileNo.trim() || !selectedCompany) {
      showAlert("error", "Mobile No and Company are required!");
      return;
    }
    setLoadingAction("request");
    const res = await postForgotPassword(mobileApiBaseURL + variables.forgotPasswordRequestOtp.url, {
      mobileNo: mobileNo.trim(),
      companyAccount: selectedCompany,
    });
    setLoadingAction("");
    if (res.ok) {
      // 200 is returned for unknown accounts too, so always move on.
      setOtp("");
      setStep("verify");
      setResendIn(RESEND_SECONDS);
      setOtpExpiresIn(OTP_LIFETIME_SECONDS);
      showAlert("success", "If the account exists, a code has been sent on WhatsApp.");
    } else {
      if (res.code === 429 && resendIn <= 0) setResendIn(RESEND_SECONDS);
      showAlert("error", res.message);
    }
  };

  const verifyOtp = async () => {
    if (otpExpiresIn <= 0) {
      showAlert("error", "The code has expired. Please resend a new code.");
      return;
    }
    const code = otp.trim();
    if (code.length !== 6) {
      showAlert("error", "Please enter the 6-digit code.");
      return;
    }
    setLoadingAction("verify");
    const res = await postForgotPassword(mobileApiBaseURL + variables.forgotPasswordVerifyOtp.url, {
      mobileNo: mobileNo.trim(),
      companyAccount: selectedCompany,
      otp: code,
    });
    setLoadingAction("");
    if (res.ok && res.data?.resetToken) {
      setResetToken(res.data.resetToken);
      setNewPass("");
      setConfirmPass("");
      setStep("reset");
    } else {
      setOtp("");
      showAlert("error", res.code === 400
        ? "Invalid or expired code. Try again or resend a new code."
        : res.message);
    }
  };

  const resetPassword = async () => {
    if (!newPass.trim()) {
      showAlert("error", "New password is required.");
      return;
    }
    if (newPass !== confirmPass) {
      showAlert("error", "Passwords do not match.");
      return;
    }
    setLoadingAction("reset");
    const res = await postForgotPassword(mobileApiBaseURL + variables.forgotPasswordReset.url, {
      resetToken,
      newPassword: newPass,
    });
    setLoadingAction("");
    if (res.ok) {
      // Server revokes all refresh tokens; drop any locally stored session too.
      dispatch(logout());
      resetState();
      dispatch(setForgetPasswordFalse());
      showAlert("success", "Password changed. Please sign in with your new password.");
    } else if (res.code === 400 && /reset token/i.test(res.message)) {
      setResetToken("");
      setNewPass("");
      setConfirmPass("");
      setOtp("");
      setResendIn(0);
      setStep("request");
      showAlert("error", "Your reset session expired. Please request a new code.");
    } else {
      showAlert("error", res.message);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value.replace(/\D/g, '').slice(0, 6));
  };

  const handleKeyPress = (action) => (event) => {
    if (event.key === "Enter" && !loading) action();
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const titles = {
    request: "Forgot your password?",
    verify: "Enter the code",
    reset: "Set a new password",
  };

  return (
    <React.Fragment>
      <Dialog
        fullScreen={fullScreen}
        open={forgetPassword}
        aria-labelledby="responsive-dialog-title"
      >

        <DialogTitle id="responsive-dialog-title">
          {titles[step]}
        </DialogTitle>
        <DialogContent>
          {step === "request" && (
            <>
              <DialogContentText>
                Enter your mobile number and company. We'll send a 6-digit code on WhatsApp.
              </DialogContentText>
              <Input type="text" placeholder="Mobile Number" setValue={setMobileNo} value={mobileNo} onKeyPress={handleKeyPress(requestOtp)} />
              <select
                className="inputComponent"
                style={{ height: "auto", width: "100%", boxSizing: "border-box" }}
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                <option value="">Select Company</option>
                {companies.map((company, index) => (
                  <option key={index} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </>
          )}

          {step === "verify" && (
            <>
              <DialogContentText>
                Enter the 6-digit code sent on WhatsApp to {mobileNo.trim()}.
              </DialogContentText>
              <DialogContentText style={{ marginTop: "8px", fontWeight: 600, color: otpExpiresIn > 0 ? "#e46e39" : "#d32f2f" }}>
                {otpExpiresIn > 0
                  ? `Code expires in ${formatCountdown(otpExpiresIn)}`
                  : "Code expired. Please resend a new code."}
              </DialogContentText>
              <Input type="text" placeholder="6-digit code" maxlength={6} setValue={handleOtpChange} value={otp} onKeyPress={handleKeyPress(verifyOtp)} />
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                <Button size="small" onClick={() => { setOtp(""); setStep("request"); }} disabled={loading} style={{ color: "#e46e39" }}>
                  Change number
                </Button>
                <Button size="small" onClick={requestOtp} disabled={loading || resendIn > 0} style={resendIn > 0 ? {} : { color: "#e46e39" }}>
                  {loadingAction === "request"
                    ? <>{spinner}Sending...</>
                    : resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </Button>
              </div>
            </>
          )}

          {step === "reset" && (
            <>
              <DialogContentText>
                Choose a new password. You'll be signed out on all devices.
              </DialogContentText>
              <Input placeholder="New Password" type="password" setValue={setNewPass} value={newPass} onKeyPress={handleKeyPress(resetPassword)} />
              <Input placeholder="Confirm New Password" type="password" setValue={setConfirmPass} value={confirmPass} onKeyPress={handleKeyPress(resetPassword)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleCloseD} style={outlinedStyle} >
            Close
          </Button>
          {step === "request" && (
            <Button variant='contained' onClick={requestOtp} disabled={loading || resendIn > 0} style={primaryStyle(!loading && resendIn > 0)}>
              {loadingAction === "request"
                ? <>{spinner}Sending...</>
                : resendIn > 0 ? `Send Code (${resendIn}s)` : "Send Code"}
            </Button>
          )}
          {step === "verify" && (
            <Button variant='contained' onClick={verifyOtp} disabled={loading || otpExpiresIn <= 0} style={primaryStyle(!loading && otpExpiresIn <= 0)}>
              {loadingAction === "verify" ? <>{spinner}Verifying...</> : "Verify"}
            </Button>
          )}
          {step === "reset" && (
            <Button variant='contained' onClick={resetPassword} disabled={loading} style={buttonStyle}>
              {loadingAction === "reset" ? <>{spinner}Resetting...</> : "Reset Password"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <PopupAlert open={showPopup} message={message} severty={severty} onClose={handleClosePopup} />

    </React.Fragment>
  );
}
